import './LearnAndEarn.scss';
import React from 'react';
import { IonText, IonButton, useIonRouter } from '@ionic/react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { uuid } from 'global';
import { IPrize } from 'models';
import { useLearnAndEarn } from 'hooks/useLearnAndEarn';
import Layout from 'components/Layout/Layout';
import Title from 'components/Title/Title';
import CardPrizeItem from 'components/CardPrize/CardPrizeItem';
import PlaceholderCardPrize from 'components/CardPrize/Placeholder';
import Freeforms from 'components/Freeforms/Freeforms';
import { useGameRules } from 'hooks';
import { usePriceVsDonations } from 'hooks/usePriceVsDonations';

const PageContent: React.FC = () => {
  const router = useIonRouter();
  const priceVsDonation = usePriceVsDonations();

  const { showGameRulesModal, isError } = useGameRules();

  const title = priceVsDonation.isLoading
    ? 'Loading...'
    : `Learn & ${priceVsDonation.value ? 'Earn' : 'Give'} game`;

  return (
    <>
      <Title text={title} />

      <Freeforms name="laegDescription" placeholderSize="paragraph" />

      <div className="ion-text-center" style={{ paddingBottom: 10 }}>
        <IonButton
          strong
          onClick={() => {
            router.push('/progress');
          }}>
          My Progress
        </IonButton>
      </div>

      {!isError && (
        <div className="ion-text-center" style={{ paddingBottom: 40 }}>
          <IonButton fill="outline" strong onClick={() => showGameRulesModal()}>
            <Freeforms name="laegRules" placeholderSize="spinner" />
          </IonButton>
        </div>
      )}

      <Freeforms name="laegPriceTitle" placeholderSize="spinner" />

      <Freeforms name="laegPricesDescription" placeholderSize="spinner" />
    </>
  );
};

const LearnAndEarn: React.FC = () => {
  const { data, isLoading, isError } = useLearnAndEarn();

  if (isError) return null;

  const footer = documentToHtmlString(data?.footer);
  const completedSet = new Set(data?.activitiesCompleted || []);
  const isEarned = (prize: IPrize) =>
    !!prize.linkedActivity?.id && completedSet.has(prize.linkedActivity.id);

  // Grand Prize is earned only when EVERY activity for the show is in the
  // user's activities_completed. Strict semantic: not "every prize-linked
  // activity" -- every activity, full stop. Forces content authors to
  // configure the show coherently before users can clear the bar.
  const allActivityIds = data?.allActivityIds || [];
  const grandPrizeEarned =
    allActivityIds.length > 0 &&
    allActivityIds.every((id) => completedSet.has(id));

  return (
    <Layout copyright={footer}>
      <div className="LearnAndEarn-page">
        <PageContent />
        {isLoading || !data ? (
          <PlaceholderCardPrize />
        ) : (
          <>
            {data.prizes.map((item: IPrize) => (
              <CardPrizeItem
                key={uuid()}
                {...item}
                earned={isEarned(item)}
              />
            ))}
            {data.grandPrize && (
              <div className="LearnAndEarn--grand-prize mt-8">
                <Title text="Grand Prize" />
                <IonText color="white">
                  <p
                    className="ion-text-center text-copy"
                    style={{ marginBottom: 15 }}>
                    Complete all activities to qualify.
                  </p>
                </IonText>
                <CardPrizeItem
                  {...data.grandPrize}
                  earned={grandPrizeEarned}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default LearnAndEarn;
